import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { FormControlDef } from '@nano/common/form';
import { PartyIdentificationType } from '@nano/common/party-identification';

import {
  PaymentAdditionalField,
  PaymentConditions,
  PaymentConfig,
  PaymentCustomField,
  PaymentDetailsType,
  PaymentInternationalParam,
  PaymentParam,
  PaymentSwiftXmlAdditionalParam,
  PaymentSwiftXmlParam,
  PaymentScheduledParam,
  PaymentCurrencyCloudAdditionalParam,
  PaymentSepaAdditionalParam,
  PaymentSwiftAdditionalParam,
  PaymentGatewayAdditionalParam,
  usesStructuredPaymentFields,
  PaymentGatewayCustomParam
} from '../payment';
import { PaymentIdentificationType } from './payment-identification/payment-identification';

export const PayeeVerificationInlineState = {
  IDLE: 'IDLE',
  VALIDATING: 'VALIDATING',
  TAKING_LONGER: 'TAKING_LONGER',
  EXCEEDED_WAIT_TIME: 'EXCEEDED_WAIT_TIME'
};

export const StructuredAddressConstants = {
  CITY_MAX_LENGTH: 35,
  PROVINCE_MAX_LENGTH: 35,
  STREET_NAME_MAX_LENGTH: 70,
  BUILDING_NUMBER_MAX_LENGTH: 16,
  BUILDING_NAME_MAX_LENGTH: 35,
  POSTAL_CODE_MAX_LENGTH: 16
};

// `paymentFields` advertises these two under names the form and POST /payments do not use, so
// without the mapping their field settings reach no control. Other parties' names already match.
const GATEWAY_PUBLISHED_FIELD_NAMES: { [publishedName: string]: string } = {
  initialPayerIdentificationCode: PaymentSepaAdditionalParam.initialPayerId,
  ultimateBeneficiaryIdentificationCode: PaymentSepaAdditionalParam.ultimateBeneficiaryId
};

const toFormFieldName = (publishedName: string): string => GATEWAY_PUBLISHED_FIELD_NAMES[publishedName] ?? publishedName;

const toFormFieldNames = (publishedNames?: string[]): string[] | undefined => publishedNames?.map(toFormFieldName);

export const normalizeGatewayConditions = (conditions: PaymentConditions): PaymentConditions => {
  if (!usesStructuredPaymentFields(conditions) || !conditions.paymentFields?.length) {
    return conditions;
  }

  const customFieldNames = Object.values(PaymentGatewayCustomParam) as string[];
  const existingAdditionalFieldNames = (conditions.additionalFields || []).map((field) => field.name);
  const existingCustomFieldNames = (conditions.customFields || []).map((field) => field.name);
  const additionalFields: PaymentAdditionalField[] = [];
  const customFields: PaymentCustomField[] = [];

  conditions.paymentFields.forEach((field) => {
    if (customFieldNames.includes(field.name)) {
      if (existingCustomFieldNames.includes(field.name)) return;
      customFields.push({
        name: field.name,
        label: field.label ?? '',
        type: field.type ?? '',
        defaultValue: field.defaultValue,
        regex: field.regex,
        glossaryId: field.glossaryId,
        mandatory: field.required,
        strictValue: field.strictValue
      });
    } else {
      const name = toFormFieldName(field.name);
      if (existingAdditionalFieldNames.includes(name)) return;
      additionalFields.push({
        name,
        defaultValue: field.defaultValue,
        regex: field.regex,
        glossaryId: field.glossaryId,
        mandatory: field.required,
        strictValue: field.strictValue,
        requiredFields: toFormFieldNames(field.requiredFields),
        incompatibleFields: toFormFieldNames(field.incompatibleFields)
      });
    }
  });

  return {
    ...conditions,
    additionalFields: [...(conditions.additionalFields || []), ...additionalFields],
    customFields: [...(conditions.customFields || []), ...customFields]
  };
};

export const GATEWAY_EDITABLE_DEFAULT_FIELDS: string[] = ['payerIdentificationType', 'payerIdentificationCodeType'];

export const GATEWAY_RESPONSE_ONLY_FIELDS: string[] = ['payerIdentificationIssueCountry', 'payerIdentificationIssueDate', 'payerIdentificationExpiryDate'];

export const PAYER_IDENTIFICATION_FIELDS: string[] = [
  PaymentSepaAdditionalParam.payerIdentification,
  PaymentSepaAdditionalParam.payerIdentificationType,
  PaymentSepaAdditionalParam.payerIdentificationCodeType,
  PaymentSepaAdditionalParam.payerId,
  PaymentSepaAdditionalParam.payerBirthDate,
  PaymentSepaAdditionalParam.payerCountryOfBirth,
  PaymentSepaAdditionalParam.payerProvinceOfBirth,
  PaymentSepaAdditionalParam.payerCityOfBirth
];

export const BENEFICIARY_IDENTIFICATION_FIELDS: string[] = [
  PaymentSepaAdditionalParam.beneficiaryIdentification,
  PaymentSepaAdditionalParam.beneficiaryIdentificationType,
  PaymentSepaAdditionalParam.beneficiaryIdentificationCodeType,
  PaymentGatewayAdditionalParam.beneficiaryIdentificationCode,
  PaymentSepaAdditionalParam.beneficiaryId,
  PaymentSepaAdditionalParam.beneficiaryBirthDate,
  PaymentSepaAdditionalParam.beneficiaryCountryOfBirth,
  PaymentSepaAdditionalParam.beneficiaryProvinceOfBirth,
  PaymentSepaAdditionalParam.beneficiaryCityOfBirth
];

export const IDENTIFICATION_CLASS_FIELDS: string[] = [
  'payerIdentification',
  'beneficiaryIdentification',
  'initialPayerIdentification',
  'ultimateBeneficiaryIdentification'
];

export const appliesGatewayDefault = (field: PaymentAdditionalField, fields: PaymentAdditionalField[]): boolean => {
  const classField = IDENTIFICATION_CLASS_FIELDS.find((name) => field.name === name || field.name === `${name}Type`);
  return !classField || !!fields.find(({ name }) => name === classField)?.mandatory;
};

/** Mirrored from `beneficiaryCategory`, never picked, so it must not require the identification type (DEV-30805). */
export const isMirroredIdentificationClass = (field: PaymentAdditionalField): boolean =>
  field.name === String(PaymentSepaAdditionalParam.beneficiaryIdentification);

export const isControlFilled = (control: AbstractControl | null | undefined): boolean => {
  if (!control?.enabled) {
    return false;
  }

  const value: unknown = control.value;

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== null && value !== undefined && value !== false;
};

export interface GatewayFieldApplicability {
  identificationControl: string;
  identificationTypeControl: string;
  identification: PartyIdentificationType[];
  identificationType: PaymentIdentificationType[];
}

const datePlaceOfBirthOnly = (party: string): GatewayFieldApplicability => ({
  identificationControl: `${party}Identification`,
  identificationTypeControl: `${party}IdentificationType`,
  identification: [PartyIdentificationType.PRIVATE],
  identificationType: [PaymentIdentificationType.DATE_PLACE_OF_BIRTH]
});

const bicBeiOrOtherOnly = (party: string): GatewayFieldApplicability => ({
  identificationControl: `${party}Identification`,
  identificationTypeControl: `${party}IdentificationType`,
  identification: [PartyIdentificationType.PRIVATE, PartyIdentificationType.ORGANISATION],
  identificationType: [PaymentIdentificationType.BIC_BEI, PaymentIdentificationType.OTHER]
});

const otherOnly = (party: string): GatewayFieldApplicability => ({
  identificationControl: `${party}Identification`,
  identificationTypeControl: `${party}IdentificationType`,
  identification: [PartyIdentificationType.PRIVATE, PartyIdentificationType.ORGANISATION],
  identificationType: [PaymentIdentificationType.OTHER]
});

export const GATEWAY_FIELD_APPLICABILITY: { [fieldName: string]: GatewayFieldApplicability } = {
  payerBirthDate: datePlaceOfBirthOnly('payer'),
  payerCountryOfBirth: datePlaceOfBirthOnly('payer'),
  payerProvinceOfBirth: datePlaceOfBirthOnly('payer'),
  payerCityOfBirth: datePlaceOfBirthOnly('payer'),
  payerId: bicBeiOrOtherOnly('payer'),
  payerIdentificationCodeType: otherOnly('payer'),

  beneficiaryBirthDate: datePlaceOfBirthOnly('beneficiary'),
  beneficiaryCountryOfBirth: datePlaceOfBirthOnly('beneficiary'),
  beneficiaryProvinceOfBirth: datePlaceOfBirthOnly('beneficiary'),
  beneficiaryCityOfBirth: datePlaceOfBirthOnly('beneficiary'),
  beneficiaryIdentificationCode: bicBeiOrOtherOnly('beneficiary'),

  initialPayerBirthDate: datePlaceOfBirthOnly('initialPayer'),
  initialPayerCountryOfBirth: datePlaceOfBirthOnly('initialPayer'),
  initialPayerProvinceOfBirth: datePlaceOfBirthOnly('initialPayer'),
  initialPayerCityOfBirth: datePlaceOfBirthOnly('initialPayer'),

  ultimateBeneficiaryBirthDate: datePlaceOfBirthOnly('ultimateBeneficiary'),
  ultimateBeneficiaryCountryOfBirth: datePlaceOfBirthOnly('ultimateBeneficiary'),
  ultimateBeneficiaryProvinceOfBirth: datePlaceOfBirthOnly('ultimateBeneficiary'),
  ultimateBeneficiaryCityOfBirth: datePlaceOfBirthOnly('ultimateBeneficiary')
};

const identificationSelection = (
  rule: GatewayFieldApplicability,
  form: AbstractControl
): { identification: PartyIdentificationType | null; identificationType: PaymentIdentificationType | null } => ({
  identification: form.get(rule.identificationControl)?.value as PartyIdentificationType | null,
  identificationType: form.get(rule.identificationTypeControl)?.value as PaymentIdentificationType | null
});

export const isShownForSelectedIdentification = (fieldName: string, form: AbstractControl | null | undefined): boolean => {
  const rule = GATEWAY_FIELD_APPLICABILITY[fieldName];
  if (!rule || !form) {
    return true;
  }

  const { identification, identificationType } = identificationSelection(rule, form);

  return rule.identification.includes(identification) && rule.identificationType.includes(identificationType);
};

export const isHiddenBySelectedIdentification = (fieldName: string, form: AbstractControl | null | undefined): boolean => {
  const rule = GATEWAY_FIELD_APPLICABILITY[fieldName];
  if (!rule || !form) {
    return false;
  }

  const { identification, identificationType } = identificationSelection(rule, form);

  return !!identification && !!identificationType && !isShownForSelectedIdentification(fieldName, form);
};

export const isIdentificationInPlay = (party: string, gatewayFieldNames: string[] = []): boolean =>
  gatewayFieldNames.includes(`${party}Identification`) || gatewayFieldNames.includes(`${party}IdentificationType`);

/** Hardcoded unless the payment uses structured `paymentFields` — `GET /conditions` omits them for CC and Visa/Mastercard. */
export const BENEFICIARY_NAME_PART_FIELDS: string[] = [
  PaymentCurrencyCloudAdditionalParam.beneficiaryIdentificationName,
  PaymentCurrencyCloudAdditionalParam.beneficiaryIdentificationSurname
];

export const PaymentFieldMaxLength = {
  beneficiaryAddress: 70,
  beneficiaryCountry: 2,
  beneficiaryPhoneNumber: 30,
  beneficiaryEmail: 1000,
  initialPayerPhoneNumber: 30,
  initialPayerEmail: 1000,
  ultimateBeneficiaryPhoneNumber: 30,
  ultimateBeneficiaryEmail: 1000,
  purposeCode: 35,
  beneficiaryCity: StructuredAddressConstants.CITY_MAX_LENGTH,
  beneficiaryProvince: StructuredAddressConstants.PROVINCE_MAX_LENGTH,
  beneficiaryStreetName: StructuredAddressConstants.STREET_NAME_MAX_LENGTH,
  beneficiaryBuildingNumber: StructuredAddressConstants.BUILDING_NUMBER_MAX_LENGTH,
  beneficiaryBuildingName: StructuredAddressConstants.BUILDING_NAME_MAX_LENGTH,
  beneficiaryPostalCode: StructuredAddressConstants.POSTAL_CODE_MAX_LENGTH,
  beneficiaryIdentificationCode: 35,
  beneficiaryIdentificationName: 70,
  beneficiaryIdentificationSurname: 70,
  paymentIdentification: 35,
  instructionIdentification: 35,
  initialPayerName: 140,
  initialPayerCity: StructuredAddressConstants.CITY_MAX_LENGTH,
  initialPayerProvince: StructuredAddressConstants.PROVINCE_MAX_LENGTH,
  initialPayerStreetName: StructuredAddressConstants.STREET_NAME_MAX_LENGTH,
  initialPayerBuildingNumber: StructuredAddressConstants.BUILDING_NUMBER_MAX_LENGTH,
  initialPayerBuildingName: StructuredAddressConstants.BUILDING_NAME_MAX_LENGTH,
  initialPayerPostalCode: StructuredAddressConstants.POSTAL_CODE_MAX_LENGTH,
  payerCity: StructuredAddressConstants.CITY_MAX_LENGTH,
  payerProvince: StructuredAddressConstants.PROVINCE_MAX_LENGTH,
  payerStreetName: StructuredAddressConstants.STREET_NAME_MAX_LENGTH,
  payerBuildingNumber: StructuredAddressConstants.BUILDING_NUMBER_MAX_LENGTH,
  payerPostalCode: StructuredAddressConstants.POSTAL_CODE_MAX_LENGTH,
  ultimateBeneficiaryName: 140,
  ultimateBeneficiaryCity: StructuredAddressConstants.CITY_MAX_LENGTH,
  ultimateBeneficiaryProvince: StructuredAddressConstants.PROVINCE_MAX_LENGTH,
  ultimateBeneficiaryStreetName: StructuredAddressConstants.STREET_NAME_MAX_LENGTH,
  ultimateBeneficiaryBuildingNumber: StructuredAddressConstants.BUILDING_NUMBER_MAX_LENGTH,
  ultimateBeneficiaryBuildingName: StructuredAddressConstants.BUILDING_NAME_MAX_LENGTH,
  ultimateBeneficiaryPostalCode: StructuredAddressConstants.POSTAL_CODE_MAX_LENGTH,
  additionalBeneficiaryBankCode: 35,
  accountInBeneficiaryBank: 35,
  correspondentBankCode: 50,
  correspondentBankName: 140,
  correspondentBankAddress: 140,
  accountWithBankCorrespondent: 35,
  additionalBeneficiaryCorrespondentBankCode: 35,
  routingCode1: 35,
  routingCode2: 35
};

export const BankTransferControls = (config: PaymentConfig): Array<FormControlDef> => [
  {
    name: PaymentParam.operationType,
    control: new UntypedFormControl(null, [Validators.required])
  },
  {
    name: PaymentParam.beneficiaryAccountNumber,
    control: new UntypedFormControl(null, [
      Validators.required,
      Validators.minLength(config.beneficiaryAccountNumberMinLength),
      Validators.maxLength(config.beneficiaryAccountNumberMaxLength),
      Validators.pattern(config.beneficiaryAccountPattern)
    ])
  },
  {
    name: PaymentParam.beneficiaryName,
    control: new UntypedFormControl(null, [
      Validators.required,
      Validators.minLength(config.beneficiaryNameMinLength),
      Validators.maxLength(config.beneficiaryNameMaxLength)
    ])
  },
  {
    name: PaymentParam.payerAccount,
    control: new UntypedFormControl(null, [Validators.required])
  },
  {
    name: PaymentParam.payerAccountNumber,
    control: new UntypedFormControl(null, [Validators.required])
  },
  {
    name: PaymentParam.amount,
    control: new UntypedFormControl(undefined, [Validators.required, Validators.min(config.amountMin), Validators.max(config.amountMax)])
  },
  {
    name: PaymentParam.currency,
    control: new UntypedFormControl(null, [Validators.required])
  },
  {
    name: PaymentParam.paymentDetailsType,
    control: new UntypedFormControl(PaymentDetailsType.UNSTRUCTURED, [Validators.required])
  },
  {
    name: PaymentParam.paymentDetails,
    control: new UntypedFormControl(null, [Validators.required, Validators.maxLength(config.detailsMaxLength)])
  },
  {
    name: PaymentParam.paymentCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.paymentCodeMaxLength)])
  },
  {
    name: PaymentParam.hasAdditionalInfo,
    control: new UntypedFormControl(null)
  },
  {
    name: PaymentParam.scheduled,
    control: new UntypedFormControl(null)
  },
  {
    name: PaymentParam.waitForFunds,
    control: new UntypedFormControl(null)
  },
  {
    name: PaymentParam.feeAccount,
    control: new UntypedFormControl({ value: null, disabled: !config.feeAccountEnabled }, [Validators.required])
  },
  {
    name: PaymentParam.feeAccountNumber,
    control: new UntypedFormControl({ value: null, disabled: !config.feeAccountEnabled }, [Validators.required])
  },
  {
    name: PaymentParam.feeAccountCurrency,
    control: new UntypedFormControl({ value: null, disabled: !config.feeAccountEnabled }, [Validators.required])
  },
  {
    name: PaymentParam.serviceChargeDetails,
    control: new UntypedFormControl({ value: null, disabled: true })
  }
];

export const BankTransferScheduledControls: Array<FormControlDef> = [
  {
    name: PaymentScheduledParam.scheduledFrequency,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentScheduledParam.scheduledStartDate,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentScheduledParam.scheduledEndDate,
    control: new UntypedFormControl({ value: null, disabled: true })
  }
];

export const BankTransferSepaAdditionalControls = (config: PaymentConfig): Array<FormControlDef> => [
  {
    name: PaymentSepaAdditionalParam.paymentIdentification,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.identificationMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.sendPayerAddress,
    control: new UntypedFormControl({ value: false, disabled: true }, [Validators.required])
  },
  {
    name: PaymentSepaAdditionalParam.initialPayerName,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.identificationNameMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.ultimateBeneficiaryName,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.identificationNameMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryCountry,
    control: new UntypedFormControl({ value: null, disabled: true })
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryAddress,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.beneficiaryAddressMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryCity,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.maxLength(StructuredAddressConstants.CITY_MAX_LENGTH),
      Validators.pattern(/\S+/)
    ])
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryProvince,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.maxLength(StructuredAddressConstants.PROVINCE_MAX_LENGTH),
      Validators.pattern(/\S+/)
    ])
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryStreetName,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.maxLength(StructuredAddressConstants.STREET_NAME_MAX_LENGTH),
      Validators.pattern(/\S+/)
    ])
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryBuildingNumber,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.maxLength(StructuredAddressConstants.BUILDING_NUMBER_MAX_LENGTH),
      Validators.pattern(/\S+/)
    ])
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryBuildingName,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.maxLength(StructuredAddressConstants.BUILDING_NAME_MAX_LENGTH),
      Validators.pattern(/\S+/)
    ])
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryPostalCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.maxLength(StructuredAddressConstants.POSTAL_CODE_MAX_LENGTH),
      Validators.pattern(/\S+/)
    ])
  }
];

export const BankTransferSepaAdditionalPayerIdentificationControls = (config: PaymentConfig): Array<FormControlDef> => [
  {
    name: PaymentSepaAdditionalParam.payerIdentification,
    control: new UntypedFormControl(null)
  },
  {
    name: PaymentSepaAdditionalParam.payerIdentificationType,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentSepaAdditionalParam.payerIdentificationCodeType,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentSepaAdditionalParam.payerId,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.payerBirthDate,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentSepaAdditionalParam.payerCountryOfBirth,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.addressCountryMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.payerProvinceOfBirth,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationProvinceOfBirthMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.payerCityOfBirth,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationCityOfBirthMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.payerCountry,
    control: new UntypedFormControl({ value: null, disabled: true })
  }
];

export const BankTransferSepaAdditionalInitialPayerIdentificationControls = (config: PaymentConfig): Array<FormControlDef> => [
  {
    name: PaymentSepaAdditionalParam.initialPayerIdentification,
    control: new UntypedFormControl(null)
  },
  {
    name: PaymentSepaAdditionalParam.initialPayerIdentificationType,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentSepaAdditionalParam.initialPayerIdentificationCodeType,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentSepaAdditionalParam.initialPayerId,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.initialPayerBirthDate,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentSepaAdditionalParam.initialPayerCountryOfBirth,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.addressCountryMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.initialPayerProvinceOfBirth,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationProvinceOfBirthMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.initialPayerCityOfBirth,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationCityOfBirthMaxLength)])
  }
];

export const BankTransferSepaAdditionalBeneficiaryIdentificationControls = (config: PaymentConfig): Array<FormControlDef> => [
  {
    name: PaymentSepaAdditionalParam.beneficiaryIdentification,
    control: new UntypedFormControl(null)
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryIdentificationType,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryIdentificationCodeType,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryId,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryBirthDate,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryCountryOfBirth,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.addressCountryMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryProvinceOfBirth,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationProvinceOfBirthMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.beneficiaryCityOfBirth,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationCityOfBirthMaxLength)])
  }
];

export const BankTransferSepaAdditionalUltimateBeneficiaryIdentificationControls = (config: PaymentConfig): Array<FormControlDef> => [
  {
    name: PaymentSepaAdditionalParam.ultimateBeneficiaryIdentification,
    control: new UntypedFormControl(null)
  },
  {
    name: PaymentSepaAdditionalParam.ultimateBeneficiaryIdentificationType,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentSepaAdditionalParam.ultimateBeneficiaryIdentificationCodeType,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentSepaAdditionalParam.ultimateBeneficiaryId,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.ultimateBeneficiaryBirthDate,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentSepaAdditionalParam.ultimateBeneficiaryCountryOfBirth,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.addressCountryMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.ultimateBeneficiaryProvinceOfBirth,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationProvinceOfBirthMaxLength)])
  },
  {
    name: PaymentSepaAdditionalParam.ultimateBeneficiaryCityOfBirth,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationCityOfBirthMaxLength)])
  }
];

export const BankTransferInternationalControls = (config: PaymentConfig): Array<FormControlDef> => [
  {
    name: PaymentInternationalParam.beneficiaryBankIdentifier,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.beneficiaryBankIdentifierMaxLength)])
  },
  {
    name: PaymentInternationalParam.beneficiaryBankName,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.beneficiaryBankNameMaxLength)])
  },
  {
    name: PaymentInternationalParam.beneficiaryBankCountry,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.addressCountryMaxLength)])
  },
  {
    name: PaymentInternationalParam.beneficiaryBankAddress,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.beneficiaryBankAddressMaxLength)])
  }
];

export const BankTransferSwiftAdditionalControls = (config: PaymentConfig): Array<FormControlDef> => [
  {
    name: PaymentSwiftAdditionalParam.beneficiaryAddress,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.beneficiaryBankAddressMaxLength)])
  },
  {
    name: PaymentSwiftAdditionalParam.correspondentBankIdentifier,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.correspondentBankIdentifierMaxLength)])
  },
  {
    name: PaymentSwiftAdditionalParam.correspondentBankName,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.correspondentBankNameMaxLength)])
  },
  {
    name: PaymentSwiftAdditionalParam.correspondentBankAddress,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.correspondentBankAddressMaxLength)])
  },
  {
    name: PaymentSwiftAdditionalParam.correspondentBankCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.correspondentBankCodeMaxLength)])
  }
];

export const BankTransferSwiftXmlControls: Array<FormControlDef> = [
  {
    name: PaymentSwiftXmlParam.instructionIdentification,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  }
];

export const BankTransferSwiftXmlAdditionalControls = (config: PaymentConfig): Array<FormControlDef> => [
  {
    name: PaymentSwiftXmlAdditionalParam.initialPayerCountry,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.addressCountryMaxLength)])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.initialPayerProvince,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.identificationProvinceOfBirthMaxLength)])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.initialPayerCity,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.identificationCityOfBirthMaxLength)])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.initialPayerStreetName,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.maxLength(StructuredAddressConstants.STREET_NAME_MAX_LENGTH),
      Validators.pattern(/\S+/)
    ])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.initialPayerBuildingName,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.maxLength(StructuredAddressConstants.BUILDING_NUMBER_MAX_LENGTH),
      Validators.pattern(/\S+/)
    ])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.initialPayerBuildingNumber,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.maxLength(StructuredAddressConstants.BUILDING_NAME_MAX_LENGTH),
      Validators.pattern(/\S+/)
    ])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.initialPayerPostalCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.beneficiaryPostalCodeMaxLength)])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.beneficiaryProvince,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.identificationProvinceOfBirthMaxLength)])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.beneficiaryCity,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.identificationCityOfBirthMaxLength)])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.beneficiaryPostalCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.beneficiaryPostalCodeMaxLength)])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryCountry,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.addressCountryMaxLength)])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryProvince,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.identificationProvinceOfBirthMaxLength)])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryCity,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.identificationCityOfBirthMaxLength)])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryStreetName,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.maxLength(StructuredAddressConstants.STREET_NAME_MAX_LENGTH),
      Validators.pattern(/\S+/)
    ])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryBuildingName,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.maxLength(StructuredAddressConstants.BUILDING_NUMBER_MAX_LENGTH),
      Validators.pattern(/\S+/)
    ])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryBuildingNumber,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.maxLength(StructuredAddressConstants.BUILDING_NAME_MAX_LENGTH),
      Validators.pattern(/\S+/)
    ])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryPostalCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.beneficiaryPostalCodeMaxLength)])
  },

  {
    name: PaymentSwiftXmlAdditionalParam.additionalBeneficiaryBankCodeType,
    control: new UntypedFormControl({ value: null, disabled: true })
  },
  {
    name: PaymentSwiftXmlAdditionalParam.additionalBeneficiaryBankCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.paymentCodeMaxLength)])
  },

  {
    name: PaymentSwiftXmlAdditionalParam.correspondentBankCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.beneficiaryBankIdentifierMaxLength)])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.correspondentBankName,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.beneficiaryBankNameMaxLength)])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.correspondentBankCountry,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.addressCountryMaxLength)])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.correspondentBankAddress,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.beneficiaryBankAddressMaxLength)])
  },

  {
    name: PaymentSwiftXmlAdditionalParam.accountWithBankCorrespondent,
    control: new UntypedFormControl(null, [
      Validators.minLength(config.beneficiaryAccountNumberMinLength),
      Validators.maxLength(config.beneficiaryAccountNumberMaxLength)
    ])
  },
  {
    name: PaymentSwiftXmlAdditionalParam.additionalBeneficiaryCorrespondentBankCodeType,
    control: new UntypedFormControl({ value: null, disabled: true })
  },
  {
    name: PaymentSwiftXmlAdditionalParam.additionalBeneficiaryCorrespondentBankCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(config.paymentCodeMaxLength)])
  },
  ...BankTransferSepaAdditionalControls(config)
];
export const BankTransferGatewayAdditionalControls = (): Array<FormControlDef> => [
  {
    name: PaymentParam.initialAmount,
    control: new UntypedFormControl({ value: undefined, disabled: true })
  },
  {
    name: PaymentParam.initialCurrency,
    control: new UntypedFormControl({ value: null, disabled: true })
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryCategory,
    control: new UntypedFormControl({ value: null, disabled: true })
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryAccountType,
    control: new UntypedFormControl({ value: null, disabled: true })
  },
  {
    name: PaymentGatewayAdditionalParam.sourceOfIncome,
    control: new UntypedFormControl({ value: null, disabled: true })
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryNationality,
    control: new UntypedFormControl({ value: null, disabled: true })
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryRegistrationCountry,
    control: new UntypedFormControl({ value: null, disabled: true })
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryIdentificationCodeType,
    control: new UntypedFormControl({ value: null, disabled: true })
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryIdentificationCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.beneficiaryIdentificationCode)])
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryAddress,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.beneficiaryAddress)])
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryCity,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.beneficiaryCity)])
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryStreetName,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.beneficiaryStreetName)])
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryBuildingNumber,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.beneficiaryBuildingNumber)])
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryBuildingName,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.beneficiaryBuildingName)])
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryProvince,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.beneficiaryProvince)])
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryPostalCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.beneficiaryPostalCode)])
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryCountry,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.minLength(PaymentFieldMaxLength.beneficiaryCountry),
      Validators.maxLength(PaymentFieldMaxLength.beneficiaryCountry)
    ])
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryPhoneNumber,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.beneficiaryPhoneNumber)])
  },
  {
    name: PaymentGatewayAdditionalParam.beneficiaryEmail,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.beneficiaryEmail)])
  },
  {
    name: PaymentGatewayAdditionalParam.initialPayerPhoneNumber,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.initialPayerPhoneNumber)])
  },
  {
    name: PaymentGatewayAdditionalParam.initialPayerEmail,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.initialPayerEmail)])
  },
  {
    name: PaymentGatewayAdditionalParam.ultimateBeneficiaryPhoneNumber,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.ultimateBeneficiaryPhoneNumber)])
  },
  {
    name: PaymentGatewayAdditionalParam.ultimateBeneficiaryEmail,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.ultimateBeneficiaryEmail)])
  },
  {
    name: PaymentGatewayAdditionalParam.purposeCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.purposeCode)])
  },
  {
    name: PaymentGatewayAdditionalParam.accountInBeneficiaryBank,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.accountInBeneficiaryBank)])
  },
  {
    name: PaymentGatewayAdditionalParam.payerCity,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.payerCity)])
  },
  {
    name: PaymentGatewayAdditionalParam.payerProvince,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.payerProvince)])
  },
  {
    name: PaymentGatewayAdditionalParam.payerStreetName,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.payerStreetName)])
  },
  {
    name: PaymentGatewayAdditionalParam.payerBuildingNumber,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.payerBuildingNumber)])
  },
  {
    name: PaymentGatewayAdditionalParam.payerPostalCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.maxLength(PaymentFieldMaxLength.payerPostalCode)])
  },
  {
    name: PaymentGatewayAdditionalParam.customFields,
    control: new UntypedFormGroup({
      [PaymentGatewayCustomParam.routingCode1]: new UntypedFormControl({ value: null, disabled: true }),
      [PaymentGatewayCustomParam.routingCode2]: new UntypedFormControl({ value: null, disabled: true })
    })
  }
];

export const BankTransferCurrencyCloudAdditionalControls = (config: PaymentConfig): Array<FormControlDef> => [
  {
    name: PaymentCurrencyCloudAdditionalParam.beneficiaryAddress,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.beneficiaryAddressMaxLength)])
  },
  {
    name: PaymentCurrencyCloudAdditionalParam.beneficiaryCountry,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.addressCountryMaxLength)])
  },
  {
    name: PaymentCurrencyCloudAdditionalParam.beneficiaryProvince,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationProvinceOfBirthMaxLength)])
  },
  {
    name: PaymentCurrencyCloudAdditionalParam.beneficiaryCity,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationCityOfBirthMaxLength)])
  },
  {
    name: PaymentCurrencyCloudAdditionalParam.beneficiaryPostalCode,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.beneficiaryPostalCodeMaxLength)])
  },
  {
    name: PaymentCurrencyCloudAdditionalParam.beneficiaryIdentification,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentCurrencyCloudAdditionalParam.beneficiaryIdentificationName,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.minLength(config.beneficiaryNameMinLength),
      Validators.maxLength(config.beneficiaryNameMaxLength)
    ])
  },
  {
    name: PaymentCurrencyCloudAdditionalParam.beneficiaryIdentificationSurname,
    control: new UntypedFormControl({ value: null, disabled: true }, [
      Validators.minLength(config.beneficiaryNameMinLength),
      Validators.maxLength(config.beneficiaryNameMaxLength)
    ])
  },
  {
    name: PaymentCurrencyCloudAdditionalParam.beneficiaryIdentificationDocument,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required])
  },
  {
    name: PaymentCurrencyCloudAdditionalParam.beneficiaryId,
    control: new UntypedFormControl({ value: null, disabled: true }, [Validators.required, Validators.maxLength(config.identificationMaxLength)])
  },
  {
    name: PaymentCurrencyCloudAdditionalParam.beneficiaryIdentificationType,
    control: new UntypedFormControl({ value: PaymentIdentificationType.OTHER, disabled: true })
  }
];
